import {Quaternion,Vector3,type Bone} from 'three';

/**
 * INERTIALIZATION (Bollo, "Inertialization: High-Performance Animation
 * Transitions in Gears of War", GDC 2018). A hard switch to a new clip keeps
 * its pose, but the difference between where the OLD motion would have been
 * this frame and the new pose becomes a per-bone offset. That offset starts
 * with the old motion's velocity and decays on a quintic to rest (zero
 * velocity and acceleration) over a few frames. The body never teleports and
 * never lingers in a crossfade: it carries its momentum into the new motion.
 *
 * Call `record` at the end of every frame's pose, `request` when a clip is
 * snapped in, and `apply` after the new frame's pose is complete.
 */
export class PoseInertializer {
  private bones:readonly Bone[]=[];
  // Last two displayed poses (local), for the source's extrapolated pose and velocity.
  private prevQ=new Float32Array(0);private prev2Q=new Float32Array(0);
  private prevP=new Float32Array(0);private prev2P=new Float32Array(0);
  private prevDt=0;private recorded=0;
  // Per-bone rotation offsets: unit axis + quintic in angle.
  private axis=new Float32Array(0);private rot=new Float32Array(0);
  // Per-bone position offsets: unit direction + quintic in distance.
  private dir=new Float32Array(0);private pos=new Float32Array(0);
  private pending=0;private pendingMax=0;private time=0;private span=0;
  private readonly q=new Quaternion();private readonly q2=new Quaternion();private readonly r=new Quaternion();
  private readonly v=new Vector3();private readonly w=new Vector3();
  get active():boolean{return this.span>0&&this.time<this.span;}

  private bind(bones:readonly Bone[]):void{
    this.bones=bones;const n=bones.length;
    this.prevQ=new Float32Array(n*4);this.prev2Q=new Float32Array(n*4);this.prevP=new Float32Array(n*3);this.prev2P=new Float32Array(n*3);
    this.axis=new Float32Array(n*3);this.rot=new Float32Array(n*7);this.dir=new Float32Array(n*3);this.pos=new Float32Array(n*7);
    this.recorded=0;this.span=0;
  }
  /** Forget history (teleports, skeleton swaps): the next transition cannot
   * extrapolate from a stale pose. */
  reset():void{this.recorded=0;this.span=0;this.pending=this.pendingMax=0;}
  /** Blend the next completed pose in over `duration` seconds; with `maxDuration`
   * the span grows with the size of the pose change (a quarter turn or more takes
   * the longer span), so a big jump is not closed as a flick. */
  request(duration=.1,maxDuration=duration):void{this.pending=Math.max(this.pending,duration);this.pendingMax=Math.max(this.pendingMax,maxDuration);}

  apply(dt:number,bones:readonly Bone[]):boolean{
    if(bones!==this.bones||bones.length*4!==this.prevQ.length)this.bind(bones);
    const h=Number.isFinite(dt)?Math.max(0,dt):0;
    if(this.pending>0){
      if(this.recorded>=2&&this.prevDt>1e-5)this.start(h,this.pending,Math.max(this.pending,this.pendingMax));
      this.pending=this.pendingMax=0;
    }
    if(!this.active)return false;
    this.time=Math.min(this.span,this.time+h);
    const t=this.time;
    for(let i=0;i<bones.length;i++){
      const angle=quintic(this.rot,i,t);
      if(Math.abs(angle)>1e-6){
        this.q.setFromAxisAngle(this.v.fromArray(this.axis,i*3),angle);
        bones[i].quaternion.premultiply(this.q);
      }
      const distance=quintic(this.pos,i,t);
      if(Math.abs(distance)>1e-7)bones[i].position.addScaledVector(this.v.fromArray(this.dir,i*3),distance);
    }
    if(this.time>=this.span)this.span=0;
    return true;
  }

  /** Remember the pose actually displayed this frame. */
  record(dt:number,bones:readonly Bone[]):void{
    if(bones!==this.bones||bones.length*4!==this.prevQ.length)this.bind(bones);
    this.prev2Q.set(this.prevQ);this.prev2P.set(this.prevP);
    for(let i=0;i<bones.length;i++){bones[i].quaternion.toArray(this.prevQ,i*4);bones[i].position.toArray(this.prevP,i*3);}
    this.prevDt=Number.isFinite(dt)?dt:0;this.recorded=Math.min(2,this.recorded+1);
  }

  private start(dt:number,minDuration:number,maxDuration:number):void{
    const bones=this.bones,step=dt>0?dt:this.prevDt,ratio=Math.min(2,step/this.prevDt);
    let duration=minDuration,largest=0;
    for(let i=0;i<bones.length;i++){
      // Source: the old motion carried one more frame at its own velocity.
      this.q.fromArray(this.prevQ,i*4);this.q2.fromArray(this.prev2Q,i*4);
      this.r.copy(this.q).multiply(this.q2.invert());if(this.r.w<0)this.r.set(-this.r.x,-this.r.y,-this.r.z,-this.r.w);
      const velocityAngle=2*Math.acos(Math.min(1,this.r.w)),s=Math.sqrt(Math.max(0,1-this.r.w*this.r.w));
      this.w.set(this.r.x,this.r.y,this.r.z);if(s>1e-6)this.w.divideScalar(s);else this.w.set(0,0,0);
      this.r.identity().slerp(this.q2.setFromAxisAngle(this.w.lengthSq()>0?this.w:this.v.set(1,0,0),velocityAngle),ratio);
      const source=this.q2.copy(this.r).multiply(this.q);
      // Offset that takes the new pose back to the source: source = offset · target.
      const offset=this.r.copy(source).multiply(this.q.copy(bones[i].quaternion).invert());
      if(offset.w<0)offset.set(-offset.x,-offset.y,-offset.z,-offset.w);
      const x0=2*Math.acos(Math.min(1,offset.w)),sin=Math.sqrt(Math.max(0,1-offset.w*offset.w));
      if(x0<1e-5||sin<1e-6){this.rot.fill(0,i*7,i*7+7);}
      else{
        this.v.set(offset.x/sin,offset.y/sin,offset.z/sin);this.v.toArray(this.axis,i*3);
        // Source angular velocity along the offset axis; the target is treated
        // as at rest (the new clip's first frames are its own motion).
        const v0=this.w.dot(this.v)*velocityAngle/this.prevDt;
        this.rot[i*7+5]=x0;this.rot[i*7+4]=v0;this.rot[i*7+6]=1;largest=Math.max(largest,x0);
      }
      // Positions (the pelvis/root and any translated bone).
      const px=this.prevP[i*3]+(this.prevP[i*3]-this.prev2P[i*3])*ratio,py=this.prevP[i*3+1]+(this.prevP[i*3+1]-this.prev2P[i*3+1])*ratio,pz=this.prevP[i*3+2]+(this.prevP[i*3+2]-this.prev2P[i*3+2])*ratio;
      this.v.set(px,py,pz).sub(bones[i].position);const d0=this.v.length();
      if(d0<1e-6){this.pos.fill(0,i*7,i*7+7);}
      else{
        this.v.divideScalar(d0);this.v.toArray(this.dir,i*3);
        const vel=this.w.set(this.prevP[i*3]-this.prev2P[i*3],this.prevP[i*3+1]-this.prev2P[i*3+1],this.prevP[i*3+2]-this.prev2P[i*3+2]).dot(this.v)/this.prevDt;
        this.pos[i*7+5]=d0;this.pos[i*7+4]=vel;this.pos[i*7+6]=1;
      }
    }
    // Size the span from the largest joint change, then fit every curve to it.
    const u=Math.min(1,Math.max(0,(largest-.4)/1.2));
    duration=minDuration+(maxDuration-minDuration)*u*u*(3-2*u);
    for(let i=0;i<bones.length;i++){
      if(this.rot[i*7+6]>0)solve(this.rot,i,this.rot[i*7+5],this.rot[i*7+4],duration);
      if(this.pos[i*7+6]>0)solve(this.pos,i,this.pos[i*7+5],this.pos[i*7+4],duration);
    }
    this.time=0;this.span=duration;
  }
}

/** Bollo's quintic: x(0)=x0, x'(0)=v0, reaching rest at t1 with no overshoot. */
function solve(out:Float32Array,i:number,x0:number,v0:number,duration:number):void{
  // Moving away from the target would overshoot: take the offset from rest.
  if(v0>0)v0=0;
  let t1=duration;
  if(v0<0)t1=Math.min(t1,-5*x0/v0);
  t1=Math.max(t1,1e-3);
  const a0=Math.max(0,(-8*v0*t1-20*x0)/(t1*t1));
  const A=-(a0*t1*t1+6*v0*t1+12*x0)/(2*t1**5);
  const B=(3*a0*t1*t1+16*v0*t1+30*x0)/(2*t1**4);
  const C=-(3*a0*t1*t1+12*v0*t1+20*x0)/(2*t1**3);
  const o=i*7;out[o]=A;out[o+1]=B;out[o+2]=C;out[o+3]=a0/2;out[o+4]=v0;out[o+5]=x0;out[o+6]=t1;
}
function quintic(c:Float32Array,i:number,t:number):number{
  const o=i*7,t1=c[o+6];if(t1<=0)return 0;
  if(t>=t1)return 0;
  return ((((c[o]*t+c[o+1])*t+c[o+2])*t+c[o+3])*t+c[o+4])*t+c[o+5];
}
